import {
  mergeStyles,
  IStyle,
  HighContrastSelector,
  IButtonStyles,
  IFontWeight,
  IsFocusVisibleClassName
} from '@fluentui/react';
import { ThemeColorPalette, ThemeColorPropertyNames, ThemeFontsCssPropertyName } from '@fluidx/office-fluid-types';
import { CommandingSurfaceStyleProps, CommandingSurfaceStyles } from './CommandingSurface';

const menuStylingConstants = {
  menuBackgroundColor: `var(${ThemeColorPropertyNames.MenuBackground}, ${ThemeColorPalette.Neutral.white})`,
  menuBorderColor: `var(${ThemeColorPropertyNames.MenuHeader}, ${ThemeColorPalette.Neutral.neutralLight})`,
  dividerBackgroundColor: `var(${ThemeColorPropertyNames.MenuDivider}, ${ThemeColorPalette.Neutral.neutralQuaternaryAlt})`
};

const buttonStylingConstants = {
  buttonFontFamily: `var(${ThemeFontsCssPropertyName.FontFamilyMenu}, Segoe UI, sans-serif)`,
  buttonFontSize: `var(${ThemeFontsCssPropertyName.FontSizeMenu}, 14px)`,
  buttonFontWeight: `var(${ThemeFontsCssPropertyName.FontWeightMenu}, 400)` as IFontWeight,
  buttonColor: `var(${ThemeColorPropertyNames.MenuItemText}, ${ThemeColorPalette.Neutral.neutralPrimary})`,
  buttonSecondaryTextColor: `var(${ThemeColorPropertyNames.BodySubtext}, ${ThemeColorPalette.Neutral.neutralSecondary})`,
  buttonDisabledColor: `var(${ThemeColorPropertyNames.DisabledBodyText}, ${ThemeColorPalette.Neutral.neutralTertiary})`,
  buttonHoverColor: `var(${ThemeColorPropertyNames.MenuItemTextHovered}, ${ThemeColorPalette.Neutral.neutralDark})`,
  buttonHoverBackgroundColor: `var(${ThemeColorPropertyNames.MenuItemBackgroundHovered}, ${ThemeColorPalette.Neutral.neutralLighter})`,
  buttonPressedBackgroundColor: `var(${ThemeColorPropertyNames.MenuItemBackgroundPressed}, ${ThemeColorPalette.Neutral.neutralLight})`,
  buttonCheckedColor: `var(${ThemeColorPropertyNames.MenuItemTextChecked}, ${ThemeColorPalette.Neutral.neutralDark})`,
  buttonCheckedBackgroundColor: `var(${ThemeColorPropertyNames.MenuItemBackgroundChecked}, ${ThemeColorPalette.Neutral.neutralLight})`,
  buttonCheckedOutlineColor: `var(${ThemeColorPropertyNames.DisabledBorder}, ${ThemeColorPalette.Neutral.neutralSecondaryAlt})`,
  outlineFocusedColor: `var(${ThemeColorPropertyNames.FocusBorder}, ${ThemeColorPalette.Neutral.black})`
};

export const getCalloutStyle = (customStyles?: string) => {
  return mergeStyles(
    {
      borderColor: menuStylingConstants.menuBorderColor,
      borderRadius: '4px',
      borderStyle: 'solid',
      borderWidth: '1px',
      backgroundColor: menuStylingConstants.menuBackgroundColor,
      selectors: {
        [HighContrastSelector]: {
          backgroundColor: 'window',
          MsHighContrastAdjust: 'none',
          forcedColorAdjust: 'none'
        }
      }
    },
    customStyles
  );
};

export const horizontalDividerStyle: IStyle = {
  height: '1px',
  margin: '4px',
  backgroundColor: menuStylingConstants.dividerBackgroundColor,
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: 'windowText',
      MsHighContrastAdjust: 'none',
      forcedColorAdjust: 'none'
    }
  }
};

const topBarDividerStyle: IStyle = mergeStyles(horizontalDividerStyle, { margin: '4px 0px' });

export const topBarDividerClassName = 'top-bar-divider';

export function getContextualMenuStyles(_props: CommandingSurfaceStyleProps): CommandingSurfaceStyles {
  return {
    root: {
      backgroundColor: menuStylingConstants.menuBackgroundColor,
      padding: '4px 0px',
      selectors: {
        [HighContrastSelector]: {
          backgroundColor: 'window'
        },
        [`.${topBarDividerClassName}`]: topBarDividerStyle,
        '.is-expanded': {
          backgroundColor: buttonStylingConstants.buttonHoverBackgroundColor,
          color: buttonStylingConstants.buttonHoverColor,
          selectors: {
            [HighContrastSelector]: {
              color: 'highlightText',
              backgroundColor: 'highlight'
            },
            span: {
              color: 'inherit'
            },
            i: {
              color: 'inherit'
            }
          }
        },
        '.is-checked': {
          color: buttonStylingConstants.buttonCheckedColor,
          backgroundColor: `${buttonStylingConstants.buttonCheckedBackgroundColor} !important`,
          outline: `1px solid ${buttonStylingConstants.buttonCheckedOutlineColor}`,
          outlineOffset: '-1px',
          selectors: {
            [HighContrastSelector]: {
              color: 'highlightText',
              backgroundColor: 'highlight !important'
            },
            ':hover': {
              backgroundColor: `${buttonStylingConstants.buttonHoverBackgroundColor} !important`,
              color: buttonStylingConstants.buttonHoverColor,
              selectors: {
                [HighContrastSelector]: {
                  color: 'highlightText',
                  backgroundColor: 'highlight !important'
                }
              }
            }
          }
        },
        '.is-disabled': {
          color: `${buttonStylingConstants.buttonDisabledColor} !important`,
          backgroundColor: `${menuStylingConstants.menuBackgroundColor} !important`,
          selectors: {
            [HighContrastSelector]: {
              color: 'grayText !important',
              backgroundColor: 'window !important'
            },
            span: {
              color: 'inherit'
            },
            i: {
              color: 'inherit'
            }
          }
        }
      }
    },
    subComponentStyles: {
      menuItem: {
        root: {
          color: buttonStylingConstants.buttonColor,
          backgroundColor: menuStylingConstants.menuBackgroundColor,
          height: 32,
          lineHeight: 32,
          paddingLeft: 8,
          paddingRight: 12,
          selectors: {
            [HighContrastSelector]: {
              color: 'windowText',
              backgroundColor: 'window'
            },
            ':hover': {
              backgroundColor: buttonStylingConstants.buttonHoverBackgroundColor,
              color: buttonStylingConstants.buttonHoverColor,
              selectors: {
                [HighContrastSelector]: {
                  color: 'highlightText',
                  backgroundColor: 'highlight'
                },
                span: {
                  color: 'inherit'
                },
                i: {
                  color: 'inherit'
                }
              }
            },
            ':active': {
              backgroundColor: buttonStylingConstants.buttonPressedBackgroundColor,
              selectors: {
                [HighContrastSelector]: {
                  color: 'highlightText',
                  backgroundColor: 'highlight'
                },
                span: {
                  color: 'inherit'
                },
                i: {
                  color: 'inherit'
                }
              }
            },
            '::after': {
              outlineColor: `${buttonStylingConstants.outlineFocusedColor} !important`,
              border: 'none !important',
              selectors: {
                [HighContrastSelector]: {
                  outlineColor: 'windowText !important'
                }
              }
            },
            [`.${IsFocusVisibleClassName} &:focus`]: {
              backgroundColor: 'inherit'
            },
            [`.${IsFocusVisibleClassName} &:focus:hover`]: {
              backgroundColor: buttonStylingConstants.buttonHoverBackgroundColor
            }
          }
        },
        icon: {
          color: 'inherit',
          marginRight: 8,
          maxHeight: 32
        },
        secondaryText: {
          color: buttonStylingConstants.buttonSecondaryTextColor,
          selectors: {
            [HighContrastSelector]: {
              color: 'inherit'
            }
          }
        },
        subMenuIcon: {
          color: buttonStylingConstants.buttonColor,
          paddingLeft: 4,
          selectors: {
            [HighContrastSelector]: {
              color: 'inherit'
            }
          }
        },
        divider: horizontalDividerStyle
      },
      callout: {}
    }
  };
}

export const topBarSectionStyles = mergeStyles({
  display: 'flex',
  flexDirection: 'row',
  listStyle: 'none',
  padding: '0px 4px',
  backgroundColor: menuStylingConstants.menuBackgroundColor,
  selectors: {
    [HighContrastSelector]: {
      backgroundColor: 'window'
    }
  }
});

const iconButtonClassNames = {
  root: 'icon-button-root',
  icon: 'icon-button-child'
};

export const iconButtonStyles: IButtonStyles = {
  root: [
    iconButtonClassNames.root,
    {
      color: buttonStylingConstants.buttonColor,
      backgroundColor: menuStylingConstants.menuBackgroundColor,
      borderRadius: '4px',
      fontSize: '16px',
      selectors: {
        [HighContrastSelector]: {
          color: 'windowText',
          backgroundColor: 'window',
          MsHighContrastAdjust: 'none',
          forcedColorAdjust: 'none'
        },
        ['::after']: {
          inset: '0px !important',
          borderRadius: '4px',
          border: `1px solid ${buttonStylingConstants.outlineFocusedColor} !important`,
          outlineColor: 'transparent !important',
          selectors: {
            [HighContrastSelector]: {
              borderColor: 'windowText !important'
            }
          }
        }
      }
    }
  ],
  rootChecked: [
    iconButtonClassNames.root,
    {
      color: buttonStylingConstants.buttonCheckedColor,
      backgroundColor: buttonStylingConstants.buttonCheckedBackgroundColor,
      border: `1px solid ${buttonStylingConstants.buttonCheckedOutlineColor}`,
      borderRadius: '4px',
      outline: 'none !important',
      selectors: {
        [HighContrastSelector]: {
          color: 'highlightText',
          backgroundColor: 'highlight',
          MsHighContrastAdjust: 'none',
          forcedColorAdjust: 'none'
        },
        ['::after']: {
          inset: '-1px !important'
        }
      }
    }
  ],
  rootDisabled: [
    iconButtonClassNames.root,
    {
      color: buttonStylingConstants.buttonDisabledColor,
      backgroundColor: menuStylingConstants.menuBackgroundColor,
      borderRadius: '4px',
      selectors: {
        [HighContrastSelector]: {
          color: 'grayText',
          backgroundColor: 'window',
          MsHighContrastAdjust: 'none',
          forcedColorAdjust: 'none'
        }
      }
    }
  ],
  iconDisabled: [
    {
      color: buttonStylingConstants.buttonDisabledColor,
      selectors: {
        [HighContrastSelector]: {
          color: 'grayText'
        }
      }
    }
  ],
  rootHovered: {
    color: `${buttonStylingConstants.buttonHoverColor} !important`,
    backgroundColor: buttonStylingConstants.buttonHoverBackgroundColor,
    selectors: {
      [HighContrastSelector]: {
        backgroundColor: 'highlight',
        color: 'highlightText',
        MsHighContrastAdjust: 'none',
        forcedColorAdjust: 'none'
      }
    }
  },
  rootCheckedHovered: {
    color: `${buttonStylingConstants.buttonHoverColor} !important`,
    backgroundColor: buttonStylingConstants.buttonHoverBackgroundColor,
    selectors: {
      [HighContrastSelector]: {
        backgroundColor: 'highlight',
        color: 'highlightText',
        MsHighContrastAdjust: 'none',
        forcedColorAdjust: 'none'
      }
    }
  },
  rootPressed: {
    backgroundColor: buttonStylingConstants.buttonPressedBackgroundColor,
    color: buttonStylingConstants.buttonColor,
    selectors: {
      [HighContrastSelector]: {
        backgroundColor: 'highlight',
        color: 'highlightText',
        MsHighContrastAdjust: 'none',
        forcedColorAdjust: 'none'
      }
    }
  },
  rootFocused: {
    color: buttonStylingConstants.buttonColor,
    selectors: {
      [HighContrastSelector]: {
        backgroundColor: 'highlight',
        color: 'highlightText',
        MsHighContrastAdjust: 'none',
        forcedColorAdjust: 'none'
      }
    }
  }
};
